import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Box, Typography, Avatar, Tabs, Tab } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import './TopPerformingInstructor.css';
import { axiosInstance } from '../../../../../utils/tokenUtils';
import '../../../SkeletonLoader/SkeletonLoader.css';

const TopPerformingInstructor = ({ timePeriod = 'last_month' }) => {
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const navigate = useNavigate();
  const instructorsLengthRef = useRef(0);

  const getTeacherType = useCallback(() => {
    return selectedTab === 0 ? 'academic' : 'creative';
  }, [selectedTab]);

  useEffect(() => {
    let isMounted = true;
    const hasInstructors = instructorsLengthRef.current > 0;

    async function fetchTopTeachers() {
      if (!hasInstructors) {
        setLoading(true);
      } else {
        setIsTransitioning(true);
      }

      try {
        const teacherType = getTeacherType();
        const res = await axiosInstance.get(`/admin/top-teachers?teacher_type=${teacherType}`);
        if (isMounted) {
          const data = res.data;
          const instructorsArray = data?.results || [];
          setInstructors(Array.isArray(instructorsArray) ? instructorsArray : []);
          instructorsLengthRef.current = instructorsArray.length;
          setIsTransitioning(false);
        }
      } catch (e) {
        if (isMounted) {
          setInstructors([]);
          instructorsLengthRef.current = 0;
          setIsTransitioning(false);
        }
      } finally {
        if (isMounted && !hasInstructors) {
          setLoading(false);
        }
      }
    }
    fetchTopTeachers();
    return () => {
      isMounted = false;
    };
  }, [timePeriod, selectedTab, getTeacherType]);

  useEffect(() => {
    instructorsLengthRef.current = instructors.length;
  }, [instructors.length]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleInstructorClick = instructorId => {
    navigate(`/admin/users/instructor-details/${instructorId}`);
  };

  const InstructorSkeleton = () => (
    <Box className="instructor-item" sx={{ cursor: 'default', pointerEvents: 'none' }}>
      <div className="skeleton-avatar" style={{ width: '40px', height: '40px' }} />
      <Box className="instructor-info" sx={{ flex: 1 }}>
        <div
          className="skeleton-text"
          style={{ width: '140px', height: '14px', marginBottom: '2px' }}
        />
      </Box>
      <Box className="instructor-rating" sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <div className="skeleton-text" style={{ width: '70px', height: '12px' }} />
      </Box>
    </Box>
  );

  return (
    <Box className="top-performing-container">
      <Typography className="top-performing-title">Top Performing Instructor</Typography>

      {/* Tabs for Academic and Creative Arts */}
      <Box className="tabs-container">
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          className="instructor-tabs"
          TabIndicatorProps={{
            className: 'tab-indicator',
          }}
        >
          <Tab label="Academic" className="tab-item" />
          <Tab label="Creative Arts" className="tab-item" />
        </Tabs>
      </Box>

      {/* Content wrapper with transition effect */}
      <Box className={`content-wrapper ${isTransitioning ? 'loading-transition' : ''}`}>
        {loading ? (
          <Box className="instructors-list">
            {[...Array(5)].map((_, index) => (
              <InstructorSkeleton key={index} />
            ))}
          </Box>
        ) : !Array.isArray(instructors) || instructors.length === 0 ? (
          <Typography className="no-instructor-message">
            No {selectedTab === 0 ? 'Academic' : 'Creative Arts'} Instructor available
          </Typography>
        ) : (
          <Box className="instructors-list">
            {instructors.map(instructor => (
              <Box
                key={instructor.id}
                className="instructor-item"
                onClick={() => handleInstructorClick(instructor.id)}
                sx={{ cursor: 'pointer' }}
              >
                <Avatar
                  src={instructor.profile_image}
                  alt={instructor.first_name + ' ' + instructor.last_name}
                  className="instructor-avatar"
                />
                <Box className="instructor-info">
                  <Typography className="instructor-name">
                    {instructor.first_name} {instructor.last_name}
                  </Typography>
                  {/* <Typography className="instructor-category">{instructor.teacher_type}</Typography> */}
                </Box>
                <Box className="instructor-rating">
                  <span className="rating-star">★</span>
                  <Typography className="rating-value">
                    {instructor.rating ? Number(instructor.rating).toFixed(1) : '0.0'} Rating
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TopPerformingInstructor;
